import Konva from "konva";
import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import {useAreaEffect} from "@/encounter/effect/AreaEffectHooks.ts";
import {useTableTopInteraction} from "@/tabletop/TableTopInteractionHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {useTableTopHistory} from "@/tabletop/history/TableTopHistoryHooks.ts";

export function useAreaEffectInteraction(effect: AreaEffect) {
    const {updateEffect} = useAreaEffect();
    const {isAreaEffectSelected, selectAreaEffect} = useTableTopSelection();
    const {startWorldTranslation} = useTableTopInteraction();
    const {beginInteraction, endInteraction} = useTableTopHistory();
    const isSelected = isAreaEffectSelected(effect);

    const handleLeftMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
        // Assigned effects are selected through their combatant's options, not on the map.
        if (effect.combatantId) return;
        selectAreaEffect(effect);
        if (effect.locked) return;

        const stage = event.target.getStage();
        if (!stage) return;

        const getMovedEffect = (delta: {x: number; y: number}) => ({
                ...effect,
                position: {
                    x: effect.position.x + delta.x,
                    y: effect.position.y + delta.y,
                },
        });
        const started = startWorldTranslation(stage, {
            onCancel: () => endInteraction(),
            onChange: delta => updateEffect(getMovedEffect(delta), "debounced"),
            onEnd: delta => {
                const movedEffect = getMovedEffect(delta);
                updateEffect(movedEffect, "immediate");
                endInteraction(movedEffect);
            },
        });
        if (started) beginInteraction({type: "areaEffect", id: effect.id});
    };

    const handleMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (event.evt.button === 0) {
            event.cancelBubble = true;
            handleLeftMouseDown(event);
        }
    };

    return {isSelected, handleMouseDown};
}
